// src/components/ReaderAudio.jsx
//
// Shared inline-audio player for the pack readers. Modelled on the prototype
// VintageAudioPlayer (between-the-lines-v2.html L9751-9827): ONE consolidated charcoal/
// gold block -- headphones + title, an episode <select> dropdown when there are multiple
// podcasts (so a group of audios does NOT stack into N tall players), and a single
// vintage slider (play/pause + time + seek).
//
// AudioList   -- the consolidated block; renders nothing when there are no audios.
// PlayerControls -- the active episode's player row. Free audio plays a rules-governed
//   Firebase download URL derived from its stored content.storageUrl (see audioUrl.js);
//   paid audio (audio._paid) resolves a signed URL LAZILY on first play via
//   getSignedUrlForItem (no schema/rules change). A resolve/playback failure shows an
//   inline notice on that player only and never crashes the page.
import React, { useState, useRef, useEffect } from 'react';
import { getSignedUrlForItem } from '../lib/callables.js';
import { freePlaybackUrl } from './audioUrl.js';

// A consolidated podcast block: title + (when >1) an episode dropdown + ONE player for
// the selected episode. Renders nothing when there are no audios.
export function AudioList({ heading, audios, packId }) {
    const list = Array.isArray(audios) ? audios : [];
    const [activeIdx, setActiveIdx] = useState(0);
    // Reset to the first episode only when the SET of audios changes (by id), not on
    // every render -- so the user's dropdown choice isn't clobbered mid-listen.
    const audioKey = list.map((a) => a.id).join('|');
    useEffect(() => { setActiveIdx(0); }, [audioKey]);

    if (list.length === 0) return null;
    const safeIdx = Math.min(activeIdx, list.length - 1);
    const active = list[safeIdx];
    const titleOf = (a) => (a && a.content && a.content.title) || (a && a.id) || 'Podcast';

    return (
        <div className="mt-8 bg-book-charcoal text-book-text border border-book-accent/40 p-5 rounded-sm">
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-3 border-b border-book-text/10 pb-3">
                <div className="flex items-center gap-2 text-book-accent min-w-0">
                    <IconHeadphones />
                    <h3 className="font-display text-sm font-bold truncate text-book-muted">{heading}</h3>
                    {active._paid && <span className="font-display text-xs text-book-accent/60 flex-shrink-0 font-semibold">Unlocked</span>}
                </div>
                {list.length > 1 ? (
                    <select value={safeIdx} onChange={(e) => setActiveIdx(Number(e.target.value))}
                        aria-label="Choose podcast episode"
                        className="bg-book-charcoal text-book-text border border-book-accent/30 rounded px-3 py-1.5 text-sm font-serif outline-none focus:border-book-accent w-full md:w-auto md:max-w-[60%]">
                        {list.map((a, i) => <option key={a.id} value={i}>{titleOf(a)}</option>)}
                    </select>
                ) : (
                    <span className="font-serif italic text-book-muted text-xs truncate">{titleOf(active)}</span>
                )}
            </div>
            {/* key forces a clean remount (reset play state/progress) when the episode changes. */}
            <PlayerControls key={active.id} audio={active} packId={packId} />
        </div>
    );
}

// The active episode's player row (no outer block/title -- AudioList owns those). Free
// audio uses content.storageUrl; paid audio resolves a signed URL lazily on first play.
function PlayerControls({ audio, packId }) {
    const audioRef = useRef(null);
    const content = audio.content || {};
    const isPaid = !!audio._paid;

    // Free audio: derive a rules-governed Firebase download URL from the stored
    // storage.googleapis.com URL (see audioUrl.js). Paid audio still resolves a signed
    // URL lazily on first play -- unchanged.
    const [src, setSrc] = useState(isPaid ? null : (content.storageUrl ? freePlaybackUrl(content.storageUrl) : null));
    const [isPlaying, setIsPlaying] = useState(false);
    const [progress, setProgress] = useState(0);
    const [duration, setDuration] = useState(0);
    const [resolving, setResolving] = useState(false);
    const [error, setError] = useState(null);
    const [pendingPlay, setPendingPlay] = useState(false);

    const resolveSrc = async () => {
        if (src) return src;
        if (!isPaid) { setError('This audio has no source.'); return null; }
        setResolving(true); setError(null);
        try {
            const { url } = await getSignedUrlForItem({ packId, itemId: audio.id });
            setSrc(url);
            return url;
        } catch (e) {
            console.error('AudioPlayer signed URL:', e);
            setError('We couldn’t load this audio. Please try again.');
            return null;
        } finally {
            setResolving(false);
        }
    };

    const togglePlay = async () => {
        const el = audioRef.current;
        if (isPlaying) { if (el) el.pause(); setIsPlaying(false); return; }
        if (src) { if (el) el.play().then(() => setIsPlaying(true)).catch(() => {}); return; }
        setPendingPlay(true);
        await resolveSrc();
    };

    useEffect(() => {
        if (pendingPlay && src && audioRef.current) {
            audioRef.current.play().then(() => setIsPlaying(true)).catch(() => {});
            setPendingPlay(false);
        }
    }, [pendingPlay, src]);

    const onTimeUpdate = () => {
        const el = audioRef.current; if (!el) return;
        const dur = el.duration;
        if (!isNaN(dur) && dur > 0) setProgress((el.currentTime / dur) * 100);
    };
    const onLoadedMetadata = () => { const el = audioRef.current; if (el) setDuration(el.duration); };
    const onSeek = (e) => {
        const el = audioRef.current; if (!el) return;
        const pct = Number(e.target.value);
        const to = (pct / 100) * duration;
        if (!isNaN(to)) { el.currentTime = to; setProgress(pct); }
    };
    const fmt = (t) => {
        if (isNaN(t) || t === Infinity) return '0:00';
        const m = Math.floor(t / 60), s = Math.floor(t % 60);
        return `${m}:${s < 10 ? '0' : ''}${s}`;
    };

    return (
        <>
            {src && (
                <audio
                    ref={audioRef}
                    src={src}
                    onPlay={() => setIsPlaying(true)}
                    onPause={() => setIsPlaying(false)}
                    onTimeUpdate={onTimeUpdate}
                    onLoadedMetadata={onLoadedMetadata}
                    onEnded={() => setIsPlaying(false)}
                    onError={() => { setError('We couldn’t play this audio.'); setIsPlaying(false); }}
                />
            )}
            {error ? (
                <p className="font-serif italic text-red-300/80 text-xs">{error}</p>
            ) : (
                <div className="flex items-center gap-2 md:gap-4 bg-book-charcoal p-3 rounded-sm border border-book-text/5">
                    <button
                        onClick={togglePlay}
                        disabled={resolving}
                        className="text-book-text hover:text-book-accent transition-colors flex-shrink-0 disabled:opacity-50"
                        aria-label={isPlaying ? 'Pause' : 'Play'}
                    >
                        {resolving
                            ? <span className="font-serif italic text-xs text-book-muted px-1">…</span>
                            : isPlaying ? <IconPause /> : <IconPlay />}
                    </button>
                    <div className="flex-grow flex items-center gap-2 md:gap-3 text-xs font-sans opacity-80 font-semibold text-book-muted">
                        <span className="w-10 text-right">{fmt((progress / 100) * duration)}</span>
                        <input
                            type="range" min="0" max="100" aria-label="Seek audio"
                            value={isNaN(progress) ? 0 : progress}
                            onChange={onSeek}
                            className="vintage-slider w-full"
                            style={{ '--vsp-played': `${isNaN(progress) ? 0 : progress}%` }}
                        />
                        {/* Total duration hides on the smallest phones to give the seek slider
                            room (current-time stays); both show from sm up (desktop unchanged). */}
                        <span className="hidden sm:inline-block w-10">{fmt(duration)}</span>
                    </div>
                </div>
            )}
        </>
    );
}

function IconHeadphones() {
    return (
        <svg className="w-4 h-4 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M3 14v-2a9 9 0 0 1 18 0v2" />
            <rect x="2" y="14" width="4" height="6" rx="1" />
            <rect x="18" y="14" width="4" height="6" rx="1" />
        </svg>
    );
}
function IconPlay() {
    return (<svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z" /></svg>);
}
function IconPause() {
    return (<svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor"><path d="M6 5h4v14H6zM14 5h4v14h-4z" /></svg>);
}
